import { FlightlineEventReview } from "../models/MissionReview"
import { DoubleReviewSplit, SingleReviewSplit } from "./DistanceSplitUtils"
import { getQCStatusForEvent } from "./MissionReviewUtils"

/*
 *  Takes the first 2 nodes from the next review
 */
export const addNewNodeReviewFromNext = (nextReview: FlightlineEventReview, feReviewId: number): SingleReviewSplit => {
    if (!nextReview.node_info) {
        throw new Error("Impossible scenario! Unable to split review from the next card. No Start or Stop node name")
    }
    const nextReviewNodeInfo = nextReview.node_info

    const startNodeName = nextReviewNodeInfo.start_node_name
    const newReview = updateOrCreateNewNodeReview(startNodeName, startNodeName + 1, { review: nextReview, feReviewId })
    const updatedNextNodeReview = updateOrCreateNewNodeReview(startNodeName + 2, nextReviewNodeInfo.stop_node_name, {
        review: nextReview,
    })
    return { newReview, updatedReview: updatedNextNodeReview }
}

/*
 *  This takes the last 2 nodes from the previous review
 */
export const addNewNodeReviewFromPrev = (prevReview: FlightlineEventReview, feReviewId: number): SingleReviewSplit => {
    if (!prevReview.node_info) {
        throw new Error("Impossible scenario! Unable to split review from previous card. No Start or Stop node name")
    }
    const prevReviewNodeInfo = prevReview.node_info

    const stopNodeName = prevReviewNodeInfo.stop_node_name
    const newReview = updateOrCreateNewNodeReview(stopNodeName - 1, stopNodeName, { review: prevReview, feReviewId })
    const updatedPrevNodeReview = updateOrCreateNewNodeReview(prevReviewNodeInfo.start_node_name, stopNodeName - 2, {
        review: prevReview,
    })
    return { newReview, updatedReview: updatedPrevNodeReview }
}

/**
 *  this is a rare scenario - and honestly is probably only possible if they're added a bunch at once
 *  this is only if both the next and previous reviews only have 3 nodes each
 *  - then we're taking 1 from each to make the new one
 */
export const addNewNodeReviewFromNextAndPrev = (
    prevReview: FlightlineEventReview,
    nextReview: FlightlineEventReview,
    feReviewId: number,
): DoubleReviewSplit => {
    if (!nextReview.node_info || !prevReview.node_info) {
        throw new Error(
            "Impossible scenario! Unable to split review from previous OR next review. No Start or Stop node name",
        )
    }
    const prevReviewNodeInfo = prevReview.node_info
    const nextReviewNodeInfo = nextReview.node_info

    // get the last node from the previous review
    const startNodeName = prevReviewNodeInfo.stop_node_name
    // get the first node from the next review
    const stopNodeName = nextReviewNodeInfo.start_node_name

    // update the prev with the last node 1 less
    const updatedPreviousReview = updateOrCreateNewNodeReview(prevReviewNodeInfo.start_node_name, startNodeName - 1, {
        review: prevReview,
    })

    const newReview = updateOrCreateNewNodeReview(startNodeName, stopNodeName, { review: prevReview, feReviewId })

    // update the next with the first node one higher
    const updatedNextReview = updateOrCreateNewNodeReview(stopNodeName + 1, nextReviewNodeInfo.stop_node_name, {
        review: nextReview,
    })

    return { newReview, updatedPreviousReview, updatedNextReview }
}

/*
 * Update relevant node fields
 *
 * Create new Segment if feReviewId is null
 */
const updateOrCreateNewNodeReview = (
    startNodeName: number,
    stopNodeName: number,
    { review, feReviewId }: { review: FlightlineEventReview; feReviewId?: number },
): FlightlineEventReview => {
    if (!review.node_info) {
        throw new Error(
            `Unable to update or create a new node review as there are no node events in review ${review.fe_review_id}`,
        )
    }
    const nodeInfo = review.node_info

    const qc_status = getQCStatusForEvent(feReviewId != null, review.qc_status)

    const startNode = nodeInfo.node_events.find((node) => node.node_original_name === startNodeName)
    const stopNode = nodeInfo.node_events.find((node) => node.node_original_name === stopNodeName)
    if (!startNode || !stopNode) {
        throw new Error(
            `Impossible scenario! Cannot find start node with name ${startNodeName} or stop node with ${stopNodeName}`,
        )
    }
    return {
        ...review,
        qc_status,
        fe_review_id: feReviewId ?? review.fe_review_id,

        // if we are adding a reviewId then this is a new card, therefore shouldn't have an id
        id: feReviewId ? undefined : review.id,

        node_info: {
            ...review.node_info,
            start_node_id: startNode.id,
            start_node_name: startNodeName,
            stop_node_id: stopNode.id,
            stop_node_name: stopNodeName,
            node_count: stopNodeName - startNodeName + 1,
        },
    }
}
